import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getAdminMenuItems, getSupervisorMenuItems, getPublikatorMenuItems } from '../../utils/menuItems';
import config from '../../config/config';
import {
  Database,
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  FileText,
  Building2,
  Calendar,
  Tag,
  Globe,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Layers,
  MapPin
} from 'lucide-react';
import '../../styles/CatalogPage.css';

const CatalogPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0
  });
  
  // Filter states
  const [search, setSearch] = useState('');
  const [klFilter, setKlFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('insert_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // Modal states
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Get user role from token (more reliable than localStorage)
  const token = localStorage.getItem('token');
  let roleNum = 0;
  try {
    if (token) {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      roleNum = decoded.role;
    }
  } catch (e) {
    console.error('Error decoding token:', e);
  }
  
  const menuItems = roleNum === 3 ? getPublikatorMenuItems() : 
                    roleNum === 2 ? getSupervisorMenuItems() : getAdminMenuItems();
  const userRoleLabel = roleNum === 3 ? 'Publikator' : roleNum === 2 ? 'Supervisor' : 'Admin';


  // Fetch catalog records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search,
        kl: klFilter,
        startDate,
        endDate,
        sortBy,
        sortOrder
      });
      
      const response = await axios.get(
        `${config.API_URL}/api/catalog/records?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setRecords(response.data.data);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }));
      }
    } catch (error) {
      console.error('Error fetching catalog records:', error);
      toast.error('Gagal mengambil data katalog');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, klFilter, startDate, endDate, sortBy, sortOrder]);
  
  // Fetch catalog statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/api/catalog/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching catalog stats:', error);
    }
  };
  
  // Fetch record detail
  const fetchRecordDetail = async (identifier) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/api/catalog/records/${encodeURIComponent(identifier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSelectedRecord(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching record detail:', error);
      toast.error('Gagal mengambil detail metadata');
    }
  };
  
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Handle filter changes
  const handleFilterChange = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchRecords();
  };
  
  // Reset filters
  const resetFilters = () => {
    setSearch('');
    setKlFilter('');
    setStartDate('');
    setEndDate('');
    setSortBy('insert_date');
    setSortOrder('DESC');
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Download XML metadata
  const downloadXml = async (record) => {
    try {
      // Jika record sudah punya xml, gunakan langsung
      if (record.xml) {
        const blob = new Blob([record.xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `metadata_${record.identifier || record.metadataIdentifier}.xml`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      
      // Jika tidak ada xml, fetch dari API
      const identifier = record.identifier || record.metadataIdentifier;
      const response = await fetch(`${config.API_URL}/api/catalog/records/${encodeURIComponent(identifier)}/xml`);
      
      if (!response.ok) {
        throw new Error('XML tidak ditemukan');
      }
      
      const xmlText = await response.text();
      const blob = new Blob([xmlText], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metadata_${identifier}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading XML:', error);
      toast.error('Gagal mengunduh XML metadata');
    }
  };
  
  // View XML in new browser tab
  const viewXmlInNewTab = (identifier) => {
    const url = `${config.API_URL}/api/catalog/records/${encodeURIComponent(identifier)}/xml`;
    window.open(url, '_blank');
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout
      menuItems={menuItems}
      userRole={userRoleLabel}
    >
      <ToastContainer position="top-center" autoClose={3000} theme="colored" />
      
      <div className="catalog-container">
        {/* Header */}
        <div className="catalog-header">
          <div className="catalog-header-content">
            <div className="catalog-title-section">
              <Database className="catalog-icon" />
              <div>
                <h1>Katalog Metadata</h1>
                <p>Kelola metadata geospasial yang telah dipublikasi ke CSW</p>
              </div>
            </div>
            <div className="catalog-header-actions">
              <button 
                className="btn-stats"
                onClick={() => setShowStatsModal(true)}
              >
                <BarChart3 size={18} />
                Statistik
              </button>
              <button 
                className="btn-refresh"
                onClick={() => fetchRecords()}
              >
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>
        
        {/* Stats Summary Cards */}
        {stats && (
          <div className="stats-summary">
            <div className="stat-card">
              <div className="stat-icon total">
                <Layers size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Total Record</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon recent">
                <Calendar size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.recentCount}</span>
                <span className="stat-label">30 Hari Terakhir</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon org">
                <Building2 size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.byOrganization?.length || 0}</span>
                <span className="stat-label">K/L Terdaftar</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon type">
                <Tag size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.byType?.length || 0}</span>
                <span className="stat-label">Jenis Data</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Filter Section */}
        <div className="catalog-filters">
          <div className="filter-row">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Cari berdasarkan judul, abstrak, keyword..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>
            
            <div className="filter-group">
              <Building2 size={16} />
              <input
                type="text"
                placeholder="Filter K/L"
                value={klFilter}
                onChange={(e) => setKlFilter(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <Calendar size={16} />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Dari tanggal"
              />
              <span>-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="Sampai tanggal"
              />
            </div>
            
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="sort-select"
            >
              <option value="insert_date-DESC">Terbaru</option>
              <option value="insert_date-ASC">Terlama</option>
              <option value="title-ASC">Judul A-Z</option>
              <option value="title-DESC">Judul Z-A</option>
              <option value="organization-ASC">K/L A-Z</option>
            </select>
            
            <button className="btn-filter" onClick={handleFilterChange}>
              <Filter size={16} />
              Filter
            </button>
            
            <button className="btn-reset" onClick={resetFilters}>
              <X size={16} />
              Reset
            </button>
          </div>
        </div>
        
        {/* Records Table */}
        <div className="catalog-table-container">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Memuat data katalog...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <Database size={48} />
              <p>Tidak ada data metadata ditemukan</p>
            </div>
          ) : (
            <table className="catalog-table">
              <thead>
                <tr>
                  <th>Identifier</th>
                  <th>Judul</th>
                  <th>Organisasi</th>
                  <th>Tipe</th>
                  <th>Tanggal Insert</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.identifier}>
                    <td className="identifier-cell">
                      <code>{record.identifier?.substring(0, 20)}...</code>
                    </td>
                    <td className="title-cell">
                      <div className="title-content">
                        <FileText size={16} />
                        <span>{record.title || '-'}</span>
                      </div>
                      {record.keywords && (
                        <div className="keywords">
                          {record.keywords.split(',').slice(0, 3).map((kw, i) => (
                            <span key={i} className="keyword-tag">{kw.trim()}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="org-cell">
                        <Building2 size={14} />
                        {record.organization || '-'}
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${record.type?.toLowerCase()}`}>
                        {record.type || '-'}
                      </span>
                    </td>
                    <td>{formatDate(record.insertDate)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action view"
                          onClick={() => fetchRecordDetail(record.identifier)}
                          title="Lihat Detail"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-action external"
                          onClick={() => viewXmlInNewTab(record.identifier)}
                          title="Lihat XML di Tab Baru"
                        >
                          <ExternalLink size={16} />
                        </button>
                        <button
                          className="btn-action download"
                          onClick={() => downloadXml(record)}
                          title="Download XML"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Pagination */}
        {!loading && records.length > 0 && (
          <div className="catalog-pagination">
            <div className="pagination-info">
              Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.totalRecords)} dari {pagination.totalRecords} record
            </div>
            <div className="pagination-controls">
              <button
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="page-number">
                Halaman {pagination.page} dari {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FileText size={20} />
                Detail Metadata ISO 19115-3
              </h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Identifikasi */}
              <div className="detail-section">
                <h3 className="section-title">📋 Identifikasi</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Metadata Identifier</label>
                    <code>{selectedRecord.metadataIdentifier || selectedRecord.identifier}</code>
                  </div>
                  <div className="detail-item">
                    <label>Edisi</label>
                    <p>{selectedRecord.edition || '-'}</p>
                  </div>
                  <div className="detail-item full-width">
                    <label>Judul</label>
                    <p className="title-text">{selectedRecord.title || '-'}</p>
                  </div>
                  {selectedRecord.alternateTitle && (
                    <div className="detail-item full-width">
                      <label>Judul Alternatif</label>
                      <p>{selectedRecord.alternateTitle}</p>
                    </div>
                  )}
                  <div className="detail-item full-width">
                    <label>Abstrak</label>
                    <p className="abstract">{selectedRecord.abstract || '-'}</p>
                  </div>
                  {selectedRecord.purpose && (
                    <div className="detail-item full-width">
                      <label>Tujuan</label>
                      <p>{selectedRecord.purpose}</p>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${selectedRecord.status?.toLowerCase()}`}>
                      {selectedRecord.status || '-'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Kategori Topik</label>
                    <p>{selectedRecord.topicCategory || '-'}</p>
                  </div>
                  {selectedRecord.credit && (
                    <div className="detail-item full-width">
                      <label>Kredit</label>
                      <p>{selectedRecord.credit}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tanggal */}
              <div className="detail-section">
                <h3 className="section-title">📅 Informasi Tanggal</h3>
                <div className="detail-grid three-columns">
                  <div className="detail-item">
                    <label>Tanggal Pembuatan</label>
                    <p>{formatDate(selectedRecord.dateCreation)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Tanggal Publikasi</label>
                    <p>{formatDate(selectedRecord.datePublication)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Tanggal Revisi</label>
                    <p>{formatDate(selectedRecord.dateRevision)}</p>
                  </div>
                </div>
              </div>

              {/* Informasi Spasial */}
              <div className="detail-section">
                <h3 className="section-title">🗺️ Informasi Spasial</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Sistem Referensi (CRS)</label>
                    <code>{selectedRecord.crs || (selectedRecord.epsgCode ? `EPSG:${selectedRecord.epsgCode}` : '-')}</code>
                  </div>
                  <div className="detail-item">
                    <label>Tipe Representasi</label>
                    <p>{selectedRecord.spatialRepresentationType || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Tipe Geometri</label>
                    <p>{selectedRecord.geometryType || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Level Topologi</label>
                    <p>{selectedRecord.topologyLevel || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Skala (Denominator)</label>
                    <p>{selectedRecord.scaleDenominator ? `1:${selectedRecord.scaleDenominator}` : '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Deskripsi Cakupan</label>
                    <p>{selectedRecord.extentDescription || '-'}</p>
                  </div>
                  {selectedRecord.boundingBox && (
                    <div className="detail-item full-width">
                      <label>Bounding Box</label>
                      <div className="bbox-grid">
                        <div className="bbox-item">
                          <span className="bbox-label">West</span>
                          <span className="bbox-value">{selectedRecord.boundingBox.west?.toFixed(6) || '-'}°</span>
                        </div>
                        <div className="bbox-item">
                          <span className="bbox-label">East</span>
                          <span className="bbox-value">{selectedRecord.boundingBox.east?.toFixed(6) || '-'}°</span>
                        </div>
                        <div className="bbox-item">
                          <span className="bbox-label">South</span>
                          <span className="bbox-value">{selectedRecord.boundingBox.south?.toFixed(6) || '-'}°</span>
                        </div>
                        <div className="bbox-item">
                          <span className="bbox-label">North</span>
                          <span className="bbox-value">{selectedRecord.boundingBox.north?.toFixed(6) || '-'}°</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Kontak Owner */}
              {selectedRecord.ownerContact && (
                <div className="detail-section">
                  <h3 className="section-title">👤 Kontak Pemilik (Owner)</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Organisasi</label>
                      <p>{selectedRecord.ownerContact.organization || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Nama</label>
                      <p>{selectedRecord.ownerContact.individualName || '-'}</p>
                    </div>
                    <div className="detail-item full-width">
                      <label>Jabatan</label>
                      <p>{selectedRecord.ownerContact.position || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Telepon</label>
                      <p>{selectedRecord.ownerContact.phone || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <p>{selectedRecord.ownerContact.email || '-'}</p>
                    </div>
                    {selectedRecord.ownerContact.address && (
                      <div className="detail-item full-width">
                        <label>Alamat</label>
                        <p>{selectedRecord.ownerContact.address}{selectedRecord.ownerContact.city ? `, ${selectedRecord.ownerContact.city}` : ''}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Kontak Custodian */}
              {selectedRecord.custodianContact && (
                <div className="detail-section">
                  <h3 className="section-title">🏢 Kontak Pemelihara (Custodian)</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Organisasi</label>
                      <p>{selectedRecord.custodianContact.organization || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Nama</label>
                      <p>{selectedRecord.custodianContact.individualName || '-'}</p>
                    </div>
                    <div className="detail-item full-width">
                      <label>Jabatan</label>
                      <p>{selectedRecord.custodianContact.position || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Telepon</label>
                      <p>{selectedRecord.custodianContact.phone || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <p>{selectedRecord.custodianContact.email || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Keywords */}
              <div className="detail-section">
                <h3 className="section-title">🏷️ Kata Kunci</h3>
                <div className="detail-grid">
                  {selectedRecord.themeKeywords?.length > 0 && (
                    <div className="detail-item full-width">
                      <label>Tema</label>
                      <div className="keywords-list">
                        {selectedRecord.themeKeywords.map((kw, i) => (
                          <span key={i} className="keyword-tag theme">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedRecord.placeKeywords?.length > 0 && (
                    <div className="detail-item full-width">
                      <label>Tempat</label>
                      <div className="keywords-list">
                        {selectedRecord.placeKeywords.map((kw, i) => (
                          <span key={i} className="keyword-tag place">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(!selectedRecord.themeKeywords?.length && !selectedRecord.placeKeywords?.length && selectedRecord.keywords) && (
                    <div className="detail-item full-width">
                      <label>Kata Kunci</label>
                      <div className="keywords-list">
                        {selectedRecord.keywords.split(',').map((kw, i) => (
                          <span key={i} className="keyword-tag">{kw.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Batasan & Distribusi */}
              <div className="detail-section">
                <h3 className="section-title">🔒 Batasan & Distribusi</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Batasan Akses</label>
                    <p>{selectedRecord.accessConstraints || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Klasifikasi</label>
                    <p>{selectedRecord.classification || '-'}</p>
                  </div>
                  {selectedRecord.useLimitation && (
                    <div className="detail-item full-width">
                      <label>Batasan Penggunaan</label>
                      <p>{selectedRecord.useLimitation}</p>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Format</label>
                    <p>{selectedRecord.format || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Bentuk Penyajian</label>
                    <p>{selectedRecord.presentationForm || '-'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Frekuensi Pemeliharaan</label>
                    <p>{selectedRecord.maintenanceFrequency || '-'}</p>
                  </div>
                  {selectedRecord.onlineResource && (
                    <div className="detail-item full-width">
                      <label>Sumber Online</label>
                      <a href={selectedRecord.onlineResource} target="_blank" rel="noopener noreferrer" className="online-link">
                        <ExternalLink size={14} />
                        {selectedRecord.onlineResource}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Lineage */}
              {selectedRecord.lineage && (
                <div className="detail-section">
                  <h3 className="section-title">📜 Silsilah (Lineage)</h3>
                  <div className="detail-grid">
                    {selectedRecord.lineage.statement && (
                      <div className="detail-item full-width">
                        <label>Pernyataan</label>
                        <p className="lineage-text">{selectedRecord.lineage.statement}</p>
                      </div>
                    )}
                    {selectedRecord.lineage.source && (
                      <div className="detail-item full-width">
                        <label>Sumber Data</label>
                        <p>{selectedRecord.lineage.source}</p>
                      </div>
                    )}
                    {selectedRecord.lineage.processSteps?.length > 0 && (
                      <div className="detail-item full-width">
                        <label>Tahapan Proses</label>
                        <ol className="process-steps">
                          {selectedRecord.lineage.processSteps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Environment */}
              {selectedRecord.environmentDescription && (
                <div className="detail-section">
                  <h3 className="section-title">💻 Lingkungan</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <label>Deskripsi Lingkungan</label>
                      <p>{selectedRecord.environmentDescription}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* XML Status */}
              <div className="xml-status">
                {selectedRecord.hasValidXml ? (
                  <span className="xml-valid">✓ XML ISO 19115-3 Valid</span>
                ) : (
                  <span className="xml-invalid">⚠ XML tidak tersedia atau format tidak valid</span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Tutup
              </button>
              <button 
                className="btn-outline"
                onClick={() => viewXmlInNewTab(selectedRecord.identifier)}
              >
                <ExternalLink size={16} />
                View XML
              </button>
              <button 
                className="btn-primary"
                onClick={() => downloadXml(selectedRecord)}
              >
                <Download size={16} />
                Download XML
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Modal */}
      {showStatsModal && stats && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="modal-content stats-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <BarChart3 size={20} />
                Statistik Katalog
              </h2>
              <button className="modal-close" onClick={() => setShowStatsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="stats-sections">
                <div className="stats-section">
                  <h3>
                    <Building2 size={16} />
                    Top 10 Organisasi
                  </h3>
                  <div className="stats-list">
                    {stats.byOrganization?.map((item, i) => (
                      <div key={i} className="stats-item">
                        <span className="stats-rank">{i + 1}</span>
                        <span className="stats-name">{item.organization}</span>
                        <span className="stats-count">{item.count}</span>
                      </div>
                    ))}
                    {(!stats.byOrganization || stats.byOrganization.length === 0) && (
                      <p className="no-data">Tidak ada data</p>
                    )}
                  </div>
                </div>
                
                <div className="stats-section">
                  <h3>
                    <Tag size={16} />
                    Berdasarkan Tipe
                  </h3>
                  <div className="stats-list">
                    {stats.byType?.map((item, i) => (
                      <div key={i} className="stats-item">
                        <span className="stats-name">{item.type || 'Unknown'}</span>
                        <span className="stats-count">{item.count}</span>
                      </div>
                    ))}
                    {(!stats.byType || stats.byType.length === 0) && (
                      <p className="no-data">Tidak ada data</p>
                    )}
                  </div>
                </div>
                
                <div className="stats-section">
                  <h3>
                    <Globe size={16} />
                    Berdasarkan Kategori Topik
                  </h3>
                  <div className="stats-list">
                    {stats.byTopicCategory?.map((item, i) => (
                      <div key={i} className="stats-item">
                        <span className="stats-name">{item.topicategory}</span>
                        <span className="stats-count">{item.count}</span>
                      </div>
                    ))}
                    {(!stats.byTopicCategory || stats.byTopicCategory.length === 0) && (
                      <p className="no-data">Tidak ada data</p>
                    )}
                  </div>
                </div>
                
                <div className="stats-section full-width">
                  <h3>
                    <Calendar size={16} />
                    Trend 12 Bulan Terakhir
                  </h3>
                  <div className="chart-container">
                    {stats.byMonth?.map((item, i) => (
                      <div key={i} className="chart-bar">
                        <div 
                          className="bar-fill"
                          style={{ 
                            height: `${(item.count / Math.max(...stats.byMonth.map(m => m.count))) * 100}%` 
                          }}
                        />
                        <span className="bar-label">{item.month.substring(5)}</span>
                        <span className="bar-value">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowStatsModal(false)}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CatalogPage;
